<script setup lang="ts">
import PlusMenuLink from '@app/layout/nav/mobile/components/PlusMenuLink.vue';
import {
  URL_OFFER_CREATE,
  URL_USER_PROFILE_MANAGE,
} from '@tools/routes/routes';
import { trans } from '@tools/translation.ts';
import Icon from '@components/Icon.vue';
import { storeToRefs } from 'pinia';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Divider from '@components/Divider.vue';
import { IconUserCircle, IconPhoneCall, IconPlus, IconMapPin } from '@icons';
import { computed } from 'vue';
import Typography from '@components/Typography.vue';
import PlusMenuItem from '@app/layout/nav/mobile/components/PlusMenuItem.vue';
import { MenuItemGroup as MenuItemGroupEnum } from '@enums/menuItemGroups.ts';
import PlusMenuItemGroup from '@app/layout/nav/mobile/components/PlusMenuItemGroup.vue';
import MobileLocationsMenu from '@app/layout/nav/mobile/components/menus/MobileLocationsMenu.vue';

const navbarStore = useNavbarStore();
const { salesPhone, internationalSalesPhone } = storeToRefs(navbarStore);
const {
  isCurrentDropdownOpen,
  setCurrentDropdownOpen,
  openLoginModal,
  closePlusMenu,
} = navbarStore;

const companyName = computed<string>(() => window.COMPANY_NAME);

const handleOpenLoginModal = (): void => {
  closePlusMenu();
  openLoginModal();
};
</script>

<template>
  <li>
    <PlusMenuLink
      :link="`tel:${internationalSalesPhone}`"
      data-cy-ref="navbar-mobile-plus__link--sales-phone"
    >
      <template #icon>
        <Icon
          :svg="IconPhoneCall"
          width="24"
          height="24"
          fill="gray-darker"
        />
      </template>

      <template #text>
        <div class="flex gap-x-1">
          <Typography variant="body">
            {{ trans('user_menu.call_company', { company_name: companyName }) }}
          </Typography>

          <Typography variant="body-bold">
            {{ salesPhone }}
          </Typography>
        </div>
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink
      :link="URL_OFFER_CREATE"
      data-cy-ref="navbar-mobile-plus__link--offer-create"
    >
      <template #icon>
        <Icon
          :svg="IconPlus"
          width="24"
          height="24"
          fill="gray-darker"
        />
      </template>

      <template #text>
        {{ trans('user_menu.main.create_offer') }}
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuItem
      data-cy-ref="navbar-mobile-plus__btn--login"
      @click="handleOpenLoginModal"
    >
      <template #icon>
        <Icon
          :svg="IconUserCircle"
          width="24"
          height="24"
          stroke="gray-darker"
        />
      </template>

      <template #text>
        {{ trans('user_menu.login_register') }}
      </template>
    </PlusMenuItem>
  </li>

  <li class="my-2 px-3">
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuItemGroup
      :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.LOCATIONS)"
      @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.LOCATIONS : null)"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconMapPin"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
          data-cy-ref="navbar-mobile-plus__btn--locations"
        />
      </template>

      <template #text>
        {{ trans('user_menu.top.locations') }}
      </template>

      <template #submenus>
        <MobileLocationsMenu
          class="mt-4"
          data-cy-ref="navbar-mobile__panel--locations"
        />
      </template>
    </PlusMenuItemGroup>
  </li>

  <li class="mt-2 px-3">
    <Divider class="m-0" color="gray" />
  </li>
</template>
